<template>
  <div class="sidebar-section">
    <div class="sidebar-section__header">
      <component
        :is="collapsible ? 'button' : 'div'"
        :type="collapsible ? 'button' : null"
        class="sidebar-section__toggle"
        :aria-expanded="collapsible ? String(!isCollapsed) : null"
        :aria-controls="collapsible ? collapseId : null"
        @click="collapsible ? (isCollapsed = !isCollapsed) : null"
      >
        <ChevronDown v-if="collapsible && !isCollapsed" :size="16" class="sidebar-section__chevron" />
        <ChevronRight v-else-if="collapsible" :size="16" class="sidebar-section__chevron" />
        <h5 class="sidebar-section__title">{{ title }}</h5>
      </component>
      <div v-if="$slots.actions" class="sidebar-section__actions">
        <slot name="actions"></slot>
      </div>
    </div>
    <b-collapse :id="collapseId" v-model="isOpen">
      <div :class="bodyClasses">
        <slot></slot>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { ChevronDown, ChevronRight } from 'lucide-vue';

export default {
  name: 'SidebarSection',
  components: { ChevronDown, ChevronRight },
  props: {
    title: { type: String, required: true },
    // Steuert, ob der Abschnitt über die Titelzeile ein- und ausklappbar ist
    collapsible: { type: Boolean, default: true },
    collapsedOnMount: { type: Boolean, default: false },
    // Rahmen und Fläche des Abschnittsrumpfs, für rahmenlose Inhalte wie die Aufgaben abschaltbar
    card: { type: Boolean, default: true },
    // Innenabstand für Inhalte, die nicht bis an den Kartenrand laufen
    padded: { type: Boolean, default: false },
  },
  data() {
    return {
      isCollapsed: this.collapsedOnMount,
    };
  },
  watch: {
    // Meldet das Auf- und Zuklappen, etwa für Inhalte, die erst beim Öffnen geladen werden
    isCollapsed(v) {
      this.$emit('toggle', !v);
    },
  },
  computed: {
    // Eigene ID je Instanz, damit mehrere Abschnitte auf einer Seite nicht dieselbe Collapse-ID teilen
    collapseId() {
      return `sidebar-section-${this._uid}`;
    },
    isOpen: {
      get() {
        return !this.isCollapsed;
      },
      set(v) {
        this.isCollapsed = !v;
      },
    },
    bodyClasses() {
      return [
        'sidebar-section__body',
        { 'sidebar-section__body--card': this.card, 'sidebar-section__body--padded': this.padded },
      ];
    },
  },
  methods: {
    open() {
      this.isCollapsed = false;
    },
    close() {
      this.isCollapsed = true;
    },
  },
};
</script>

<style scoped>
.sidebar-section__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.sidebar-section__toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--text);
  text-align: left;
  user-select: none;
}
button.sidebar-section__toggle {
  cursor: pointer;
}
.sidebar-section__toggle:focus {
  outline: none;
}
.sidebar-section__toggle:focus-visible {
  border-radius: var(--radius-sm);
  box-shadow: var(--focus-ring);
}
.sidebar-section__chevron {
  flex-shrink: 0;
  color: var(--text-muted);
}
.sidebar-section__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: var(--text);
}
.sidebar-section__actions {
  display: flex;
  margin-left: auto;
  align-items: center;
  gap: 8px;
}
.sidebar-section__body--card {
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background-color: var(--surface);
}
.sidebar-section__body--padded {
  padding: 16px;
}
</style>
